/**
 * SIL-1347 - the marketplace URL search contract. Spec section 4.1:
 * `validateSearch` with Zod over `q`, `model[]`, `type[]`, `condition[]`,
 * `region[]`, `price_min`, `price_max`, and the page sends ALL of them to
 * `GET /api/client/marketplace`.
 *
 * Two halves, deliberately in one module and out of the route file so a test can
 * exercise the round trip without mounting the page:
 *
 *  - `marketplaceSearchSchema` parses the URL. Every field `.catch(undefined)`s:
 *    a hand-edited or stale link drops the bad param and still renders the page,
 *    rather than throwing the router into an error boundary over a typo.
 *  - `marketplaceApiQuery` projects the parsed search onto the API's query
 *    params. It is a straight 1:1 mapping - there is deliberately no client-side
 *    filtering anywhere in this screen. In particular the price filter is NOT
 *    special-cased for an unverified buyer: the server binds it to the bucketed
 *    range bounds (spec section 5.3), and a client that re-implemented that would
 *    be a second, drifting copy of the rule that stops the exact price leaking.
 */
import { z } from 'zod';
import { conditionBandSchema } from '@siliconx/shared/dto/asset';
import { listingTypeSchema } from '@siliconx/shared/dto/listing';

/**
 * A URL param that may be a single value or a list. TanStack Router's default
 * `parseSearch` JSON-decodes values, so a multi-select round-trips as a real
 * array - but a hand-written `?model=H100` arrives as a bare string, and that
 * link should work too.
 */
const list = <T extends z.ZodTypeAny>(item: T) =>
  z.preprocess(
    (v) => (Array.isArray(v) ? v : v === undefined || v === null || v === '' ? undefined : [v]),
    z.array(item).min(1).optional(),
  );

/** A price bound. Numbers arrive as numbers from `parseSearch`, strings from a typed URL. */
const price = z.preprocess(
  (v) => (v === undefined || v === null || v === '' ? undefined : Number(v)),
  z.number().finite().nonnegative().optional(),
);

export const marketplaceSearchSchema = z.object({
  q: z.string().trim().min(1).max(200).optional().catch(undefined),
  model: list(z.string().min(1)).catch(undefined),
  type: list(listingTypeSchema).catch(undefined),
  condition: list(conditionBandSchema).catch(undefined),
  region: list(z.string().min(1)).catch(undefined),
  /** SIL-1444 - the asset-type checkbox group (spec Change 3). */
  asset_type: list(z.string().min(1)).catch(undefined),
  price_min: price.catch(undefined),
  price_max: price.catch(undefined),
});

export type MarketplaceSearch = z.infer<typeof marketplaceSearchSchema>;

/** Every array-valued facet, so the filter card and the tests share one list. */
export const MARKETPLACE_FACET_KEYS = ['model', 'type', 'condition', 'region', 'asset_type'] as const;
export type MarketplaceFacetKey = (typeof MARKETPLACE_FACET_KEYS)[number];

export const EMPTY_MARKETPLACE_SEARCH: MarketplaceSearch = {
  q: undefined,
  model: undefined,
  type: undefined,
  condition: undefined,
  region: undefined,
  asset_type: undefined,
  price_min: undefined,
  price_max: undefined,
};

/** True when anything is narrowing the result set - drives the "Clear" affordance. */
export function hasActiveMarketplaceSearch(search: MarketplaceSearch): boolean {
  return (
    Boolean(search.q) ||
    MARKETPLACE_FACET_KEYS.some((k) => (search[k]?.length ?? 0) > 0) ||
    search.price_min !== undefined ||
    search.price_max !== undefined
  );
}

/**
 * The parsed search as `GET /api/client/marketplace` query params. Array values
 * stay arrays: the Hono client serialises them as repeated params, which is what
 * `marketplaceFilterSchema`'s `repeatable()` normalises on the way in.
 *
 * An empty facet array is omitted entirely rather than sent as a zero-length
 * list - "no boxes ticked in this group" means "do not filter on this group",
 * and an empty `IN ()` would mean the opposite.
 */
export function marketplaceApiQuery(search: MarketplaceSearch): Record<string, string | string[]> {
  const query: Record<string, string | string[]> = {};
  if (search.q) query.q = search.q;
  for (const key of MARKETPLACE_FACET_KEYS) {
    const values = search[key];
    if (values && values.length > 0) query[key] = [...values];
  }
  if (search.price_min !== undefined) query.price_min = String(search.price_min);
  if (search.price_max !== undefined) query.price_max = String(search.price_max);
  return query;
}

/**
 * Toggle one value in a facet group, returning the next search. Removing the
 * last value of a group yields `undefined`, not `[]`, so the param leaves the URL
 * instead of lingering as an empty array.
 */
export function toggleFacet(
  search: MarketplaceSearch,
  key: MarketplaceFacetKey,
  value: string,
): MarketplaceSearch {
  const current = (search[key] as string[] | undefined) ?? [];
  const next = current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];
  return { ...search, [key]: next.length > 0 ? next : undefined } as MarketplaceSearch;
}
